import { ulid } from 'ulid';
import { EventDispatcher, IEvent } from 'ste-events';
import Cookies from 'js-cookie';
import { SessionProperties } from '../models';
import { SessionIdentity } from '@samelogic/steps';
import { SLUser } from '../../models';

const PROPS_COOKIE_PREFIX = '_sl_props';
const ID_COOKIE_PREFIX = '_sl_id';

export interface OnPropertiesChangedEvent {
  property: 'properties';
  value: SessionProperties;
}

export interface OnIdentityChangedEvent {
  property: 'identity';
  value: SessionIdentity;
}

type OnChangedEvent = OnPropertiesChangedEvent | OnIdentityChangedEvent;
export class SessionManager {
  // We need to use SameSite=None for iframe support
  private readonly cookieApi = Cookies.withAttributes({ sameSite: 'None', secure: true });
  private _onChanged = new EventDispatcher<SessionManager, OnChangedEvent>();

  constructor(private readonly envId: string, user?: SLUser) {
    this.setUser(user);

    this.setSessionProperties({
      envId: this.envId,
      projId: '',
      orgId: ''
    });
  }

  public setSessionProperties(sessionProperties: SessionProperties): void {
    this.cookieApi.set(`${PROPS_COOKIE_PREFIX}.${this.envId}`, JSON.stringify(sessionProperties));
    this._onChanged.dispatch(this, {
      property: 'properties',
      value: sessionProperties
    });
  }

  public setSessionIdentity(sessionIdentity: SessionIdentity): void {
    this.cookieApi.set(`${ID_COOKIE_PREFIX}.${this.envId}`, JSON.stringify(sessionIdentity));
    this._onChanged.dispatch(this, {
      property: 'identity',
      value: sessionIdentity
    });
  }

  public setUser(user: SLUser = { key: ulid() }): void {
    if (user.key === undefined) {
      user.key = ulid();
    }

    this.cookieApi.set(`${ID_COOKIE_PREFIX}.${this.envId}`, JSON.stringify(user));
  }

  public getUser(): SLUser | null {
    const cookieUserString = this.cookieApi.get(`${ID_COOKIE_PREFIX}.${this.envId}`);
    return cookieUserString ? (JSON.parse(cookieUserString) as SLUser) : null;
  }

  public getBase64User(): string {
    const cookieUserString = this.cookieApi.get(`${ID_COOKIE_PREFIX}.${this.envId}`);
    return cookieUserString ? btoa(cookieUserString) : '';
  }

  /**
   * Get the session identity from the cookie. If it does not exist, one will be created.
   */
  public getSessionIdentity(): SessionIdentity {
    const identity = this.getIdentity();
    if (identity === null) {
      throw new Error('Session identity not found');
    }

    return identity;
  }

  private getIdentity(): SessionIdentity | null {
    const sessionIdentityString = this.cookieApi.get(`${ID_COOKIE_PREFIX}.${this.envId}`);
    return sessionIdentityString ? (JSON.parse(sessionIdentityString) as SessionIdentity) : null;
  }

  public resetSessionIdentity(): void {
    this.setSessionIdentity({
      sessionId: ulid(),
      userId: ulid()
    });
  }

  public get onChanged(): IEvent<SessionManager, OnChangedEvent> {
    return this._onChanged.asEvent();
  }
}
